import React, { useEffect, useMemo, useState } from 'react';
import { Spinner, useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

import {
  baixarRelacaoDaCarteira,
  CodigoRegime,
  IFiltroCarteira,
  Marca,
  Parada,
  Parecer,
  Situacao,
  SubSituacao,
  TipoBusca,
  useFechamentoCalendario,
  useFechamentoCarteira,
  useFechamentoInativas,
} from '../../../../hooks/useFechamentoContabilMensal';
import { BASE_ROTA, periodoPadrao } from './regras';
import {
  alternarMarcada,
  chaveDaMarcada,
  faixaDeMeses,
  IEmpresaMarcada,
  IPeriodoCarteira,
  lerSelecao,
  mesesDoPeriodo,
  moverPeriodo,
  NOMES_MES,
  numero,
  PARECERES_DO_PAINEL,
  SITUACOES,
  situacaoDaTela,
  textoDoPeriodo,
  tituloDoPeriodo,
  ultimoMesDe,
} from './regrasDaCarteira';
import { Calendario } from './carteira/Calendario';
import { CartaoDaEmpresa, CartaoInativa } from './carteira/Cartoes';
import { AbaDoDetalhe } from './carteira/detalhe/DetalheDaEmpresa';
import { DetalheDaSelecao } from './carteira/detalhe/DetalheDaSelecao';
import { Circuito, Lua, Sol, Voltar } from './carteira/icones';
import { OQueFalta } from './carteira/OQueFalta';
import { Paginacao } from './carteira/Paginacao';
import { Painel } from './carteira/Painel';
import { PeriodoDaCarteira } from './carteira/PeriodoDaCarteira';
import './carteira.css';

const urlDaEmpresa = (id: number, competencia: string, tela = 'resumo') =>
  `${BASE_ROTA}/empresa?empresa=${id}&comp=${competencia}&tela=${tela}`;

const TELA_DO_MODULO: Record<string, string> = {
  constatacoes: 'constatacoes',
  conciliacao: 'constatacoes',
  cruzamentos: 'constatacoes',
  demonstracoes: 'constatacoes',
};

const TIPOS_BUSCA: [TipoBusca, string, string][] = [
  ['razao', 'RAZÃO', 'Todas — digite a razão social'],
  ['cnpj', 'CNPJ', 'Todas — digite o CNPJ'],
  ['id', 'ID', 'Todas — digite o código'],
];

const ROTULO: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  color: '#64748B',
};

const cpChip = (
  ativo: boolean,
  cor: string,
  suave: string,
  texto: string,
): React.CSSProperties => ({
  padding: '5px 12px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  border: `1.5px solid ${ativo ? cor : '#E4E4E7'}`,
  background: ativo ? suave : '#FFFFFF',
  color: ativo ? texto : '#3F3F46',
});

/** Chip tirado da lista pelo x: apagado e riscado, o clique traz de volta. */
const CHIP_TIRADO: React.CSSProperties = {
  border: '1.5px dashed #CBD5E1',
  background: '#F8FAFC',
  color: '#94A3B8',
  textDecoration: 'line-through',
};

/**
 * Chip com o x que tira da lista as empresas que ele conta. O x é um botão à parte,
 * por cima do canto do chip (botão dentro de botão não vale).
 */
function ChipComX({
  rotulo,
  tirado,
  ativo,
  estilo,
  disabled,
  title,
  onClick,
  onTirar,
}: {
  rotulo: string;
  tirado: boolean;
  ativo: boolean;
  estilo: React.CSSProperties;
  disabled: boolean;
  title?: string;
  onClick: () => void;
  onTirar: () => void;
}) {
  return (
    <span className="chipX">
      <button
        type="button"
        className="chip"
        aria-pressed={ativo}
        disabled={disabled}
        onClick={tirado ? onTirar : onClick}
        title={tirado ? 'Fora da lista · clique para voltar a mostrar' : title}
        style={{ ...estilo, ...(tirado ? CHIP_TIRADO : {}), paddingRight: 28 }}
      >
        {rotulo}
      </button>
      <button
        type="button"
        className="chipXBotao"
        disabled={disabled}
        onClick={onTirar}
        aria-label={tirado ? 'Voltar a mostrar estas empresas' : 'Tirar estas empresas da lista'}
        title={tirado ? 'Voltar a mostrar' : 'Tirar estas empresas da lista'}
      >
        {tirado ? '\u21BA' : '\u2715'}
      </button>
    </span>
  );
}

/** "09/2026" ou "03/2026 a 05/2026" dos meses marcados no calendário. */
const textoDosMeses = (competencias: string[]): string => {
  const ord = [...competencias].sort();
  const mmaaaa = (c: string) => `${c.slice(5, 7)}/${c.slice(0, 4)}`;
  return ord.length === 1 || ord[0] === ord[ord.length - 1]
    ? mmaaaa(ord[0])
    : `${mmaaaa(ord[0])} a ${mmaaaa(ord[ord.length - 1])}`;
};

export function FechamentoContabilMensalPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const hoje = useMemo(() => new Date(), []);
  const padrao = useMemo(() => periodoPadrao(hoje), [hoje]);
  const anoPadrao = Number(padrao.fim.slice(0, 4));
  const mesPadrao = Number(padrao.fim.slice(5, 7)) - 1;

  const [escuro, setEscuro] = useState(false);
  const [ano, setAno] = useState(anoPadrao);
  const [regimes, setRegimes] = useState<CodigoRegime[]>([]);
  // meses marcados no calendário ("AAAA-MM:situação") e meses com as inativas abertas
  const [sel, setSel] = useState<string[]>([]);
  const [inatSel, setInatSel] = useState<string[]>([]);
  const [periodo, setPeriodo] = useState<IPeriodoCarteira>({
    anos: [anoPadrao],
    mIni: mesPadrao,
    mFim: mesPadrao,
    tipo: 'mensal',
  });
  const [situacao, setSituacao] = useState<Situacao | ''>('');
  const [sub, setSub] = useState<SubSituacao | ''>('');
  const [marca, setMarca] = useState<Marca>('');
  // chips tirados da lista pelo x
  const [excluir, setExcluir] = useState<string[]>([]);
  const [etapa, setEtapa] = useState<Parada>('');
  const [parecer, setParecer] = useState<Parecer | ''>('');
  const [ordem, setOrdem] = useState<'receita' | 'criticos'>('receita');
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [tipoBusca, setTipoBusca] = useState<TipoBusca>('razao');
  const [pagina, setPagina] = useState(1);
  // empresas marcadas nos cards (clique simples): detalhe ou carrossel abaixo
  const [marcadas, setMarcadas] = useState<IEmpresaMarcada[]>([]);
  const [noCarrossel, setNoCarrossel] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setBuscaAplicada(busca.trim());
      setPagina(1);
    }, 350);
    return () => clearTimeout(t);
  }, [busca]);

  const mudar = (acao: () => void) => {
    acao();
    setPagina(1);
  };

  const cal = useFechamentoCalendario(ano, regimes);
  const anos = useMemo(() => {
    const lista = new Set<number>(cal.data?.anos ?? []);
    if (ultimoMesDe(hoje.getFullYear(), hoje) >= 0) lista.add(hoje.getFullYear());
    lista.add(anoPadrao);
    return Array.from(lista).sort((a, b) => a - b);
  }, [cal.data?.anos, hoje, anoPadrao]);

  const mesesPeriodo = useMemo(() => mesesDoPeriodo(periodo, hoje), [periodo, hoje]);
  const marcados = useMemo(() => sel.map(lerSelecao), [sel]);
  const mesesMarcados = Array.from(new Set(marcados.map(m => m.competencia))).sort();
  const gruposMarcados = Array.from(new Set(marcados.map(m => m.situacao)));
  // situação única escolhida (no calendário ou no chip): os chips de detalhe aparecem
  let grupoFixo: Situacao | null = situacao || null;
  if (sel.length) grupoFixo = gruposMarcados.length === 1 ? gruposMarcados[0] : null;
  const inativasAbertas = inatSel.length > 0;

  const filtro: IFiltroCarteira = {
    ini: mesesPeriodo[0] ?? padrao.fim,
    fim: mesesPeriodo[mesesPeriodo.length - 1] ?? padrao.fim,
    meses: periodo.anos.length > 1 ? mesesPeriodo : undefined,
    regimes,
    busca: buscaAplicada,
    tipoBusca,
    marca,
    etapa,
    ordem,
    pagina,
    selecao: sel.length ? sel : undefined,
    situacao: sel.length ? '' : situacao,
    sub: grupoFixo ? sub : '',
    excluir: excluir.length ? excluir : undefined,
  };
  const { data, isFetching, isError, error } = useFechamentoCarteira(filtro);
  const inativas = useFechamentoInativas(
    {
      ...filtro,
      meses: inatSel,
      ini: inatSel[0] ?? filtro.ini,
      fim: inatSel[inatSel.length - 1] ?? filtro.fim,
    },
    inativasAbertas,
  );

  // relação em Excel no mesmo recorte dos cards (todas as páginas)
  const [exportando, setExportando] = useState(false);
  const exportar = async () => {
    setExportando(true);
    try {
      await baixarRelacaoDaCarteira(filtro);
    } catch {
      toast({
        status: 'error',
        title: 'Não foi possível gerar a relação',
        description: 'Tente de novo em instantes.',
        isClosable: true,
      });
    } finally {
      setExportando(false);
    }
  };

  const abrir = (id: number, competencia: string, tela = 'resumo') =>
    navigate(urlDaEmpresa(id, competencia, tela));
  const marcar = (e: IEmpresaMarcada) => {
    const nova = alternarMarcada(marcadas, e);
    const entrou = nova.length > marcadas.length;
    setMarcadas(nova);
    // a que entra vira a atual; a que sai deixa a posição dentro da lista
    setNoCarrossel(atual =>
      entrou ? nova.length - 1 : Math.min(atual, Math.max(0, nova.length - 1)),
    );
  };
  const chavesMarcadas = new Set(marcadas.map(chaveDaMarcada));

  // ---- calendário ----
  const mudarAno = (dir: -1 | 1) => {
    const novo = ano + dir;
    const ult = ultimoMesDe(novo, hoje);
    mudar(() => {
      setAno(novo);
      setSel([]);
      setInatSel([]);
      setSub('');
      setSituacao('');
      if (ult >= 0) setPeriodo({ anos: [novo], mIni: ult, mFim: ult, tipo: 'mensal' });
    });
  };
  const mesesCal = cal.data?.meses ?? [];
  const pareceres = useMemo(() => {
    const soma = Object.fromEntries(PARECERES_DO_PAINEL.map(p => [p.chave, 0])) as Record<
      Parecer,
      number
    >;
    mesesCal
      .filter(m => (mesesMarcados.length ? mesesMarcados.includes(m.competencia) : true))
      .filter(
        m =>
          m.competencia !== `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`,
      )
      .forEach(m => {
        PARECERES_DO_PAINEL.forEach(p => {
          soma[p.chave] += m.pareceres[p.chave] ?? 0;
        });
      });
    return soma;
  }, [mesesCal, mesesMarcados, hoje]);
  const alvoDosPareceres = mesesMarcados.length
    ? `${faixaDeMeses(mesesMarcados.map(c => Number(c.slice(5, 7)) - 1))}/${ano}`
    : String(ano);

  // brilho de fundo: acende a cor de cada situação marcada no calendário
  const acesa = (s: Situacao) => (gruposMarcados.includes(s) ? 1 : 0);

  // ---- carteira: título, resumo e período ----
  const contagens = data?.contagens;
  const variosAnos =
    periodo.anos.length > 1 || new Set(mesesMarcados.map(c => c.slice(0, 4))).size > 1;
  let titulo = tituloDoPeriodo(periodo) + (situacao ? ` · ${situacaoDaTela(situacao).nome}` : '');
  let corTitulo = situacao ? situacaoDaTela(situacao).cor : '#1B6FD0';
  if (sel.length) {
    const unico = marcados.length === 1 ? marcados[0] : null;
    titulo = unico
      ? `${situacaoDaTela(unico.situacao).nome} · ${NOMES_MES[Number(unico.competencia.slice(5, 7)) - 1]} de ${unico.competencia.slice(0, 4)}`
      : `Seleção · ${gruposMarcados.length} ${gruposMarcados.length > 1 ? 'situações' : 'situação'} em ${faixaDeMeses(
          mesesMarcados.map(c => Number(c.slice(5, 7)) - 1),
        )}/${ano}`;
    corTitulo = grupoFixo ? situacaoDaTela(grupoFixo).cor : '#1B6FD0';
  }
  if (inativasAbertas) {
    titulo = `Inativas · ${
      inatSel.length === 1
        ? `${NOMES_MES[Number(inatSel[0].slice(5, 7)) - 1]} de ${inatSel[0].slice(0, 4)}`
        : `${faixaDeMeses(inatSel.map(c => Number(c.slice(5, 7)) - 1))}/${inatSel[0].slice(0, 4)}`
    }`;
    corTitulo = '#94A3B8';
  }
  const subRotulo =
    grupoFixo && sub ? situacaoDaTela(grupoFixo).subs.find(s => s.chave === sub)?.rotulo : '';
  const muitosMeses = sel.length ? mesesMarcados.length > 1 : mesesPeriodo.length > 1;
  let resumo = '';
  if (inativasAbertas) {
    resumo = buscaAplicada
      ? `${numero(inativas.data?.total ?? 0)} encontrada(s) para “${buscaAplicada}”`
      : `${numero(inativas.data?.total ?? 0)} empresas sem revisão no período (fora do total)`;
  } else if (data) {
    resumo = buscaAplicada
      ? `${numero(data.total)} encontrada(s) para “${buscaAplicada}”`
      : `${numero(data.total)} empresas${subRotulo ? ` · ${subRotulo}` : ''}${
          muitosMeses ? ` · ${numero(data.total_competencias)} competências` : ''
        }`;
  }
  const textoPeriodo = sel.length ? textoDosMeses(mesesMarcados) : textoDoPeriodo(periodo);
  const anoMinimo = anos[0] ?? anoPadrao;

  // setas do período: com meses marcados, andam a seleção dentro do ano do calendário
  const moverSel = (dir: -1 | 1) => {
    const novos = marcados.map(m => {
      const mes = Number(m.competencia.slice(5, 7)) - 1 + dir;
      return { ...m, mes };
    });
    if (novos.some(n => n.mes < 0 || n.mes > ultimoMesDe(ano, hoje))) return null;
    return novos.map(n => `${ano}-${String(n.mes + 1).padStart(2, '0')}:${n.situacao}`);
  };
  const destino = (dir: -1 | 1) =>
    sel.length ? moverSel(dir) : moverPeriodo(periodo, dir, hoje, anoMinimo);
  const mover = (dir: -1 | 1) => {
    const d = destino(dir);
    if (!d) return;
    mudar(() => {
      if (Array.isArray(d)) setSel(d);
      else {
        setPeriodo(d);
        const anoAlvo = Math.max(...d.anos);
        if (!d.anos.includes(ano) || d.anos.length === 1) setAno(anoAlvo);
      }
    });
  };

  // x do chip: tira da lista (e solta o filtro dele, se estava escolhido) ou traz de volta
  const tirar = (chave: string) =>
    mudar(() => {
      if (excluir.includes(chave)) {
        setExcluir(excluir.filter(c => c !== chave));
        return;
      }
      setExcluir([...excluir, chave]);
      if (situacao === chave) {
        setSituacao('');
        setSub('');
      }
      if (marca === chave) setMarca('');
      if (chave === 'inativas') setInatSel([]);
    });

  // ---- chips de situação ----
  const chipsSituacao = sel.length
    ? SITUACOES.filter(s => gruposMarcados.includes(s.chave)).map(s => {
        const meses = marcados
          .filter(m => m.situacao === s.chave)
          .map(m => Number(m.competencia.slice(5, 7)) - 1);
        return {
          chave: s.chave,
          rotulo: `${s.nome} · ${faixaDeMeses(meses)}`,
          qtd: contagens?.situacoes[s.chave]?.qtd ?? 0,
          x: '  ✕',
          exclusao: '',
          ativo: true,
          estilo: cpChip(true, s.cor, s.suave, s.texto),
          pick: () => mudar(() => setSel(sel.filter(k => lerSelecao(k).situacao !== s.chave))),
        };
      })
    : [
        {
          chave: 'todas',
          rotulo: 'Todas',
          qtd: contagens?.total ?? 0,
          x: '',
          exclusao: '',
          ativo: !situacao,
          estilo: cpChip(!situacao, '#1B6FD0', '#E9F4FD', '#155FB0'),
          pick: () =>
            mudar(() => {
              setSituacao('');
              setSub('');
            }),
        },
        ...SITUACOES.map(s => ({
          chave: s.chave,
          rotulo: s.nome,
          qtd: contagens?.tirados?.[s.chave] ?? contagens?.situacoes[s.chave]?.qtd ?? 0,
          x: '',
          exclusao: s.chave as string,
          ativo: situacao === s.chave,
          estilo: cpChip(situacao === s.chave, s.cor, s.suave, s.texto),
          pick: () =>
            mudar(() => {
              setSituacao(situacao === s.chave ? '' : s.chave);
              setSub('');
            }),
        })),
      ];
  const grupoDoDetalhe = grupoFixo ? situacaoDaTela(grupoFixo) : null;
  const marcaChip = (chave: Marca, rotulo: string, qtd: number) => {
    const ativo = marca === chave;
    return (
      <ChipComX
        rotulo={`${rotulo}: ${numero(qtd)}`}
        tirado={excluir.includes(chave)}
        ativo={ativo}
        estilo={cpChip(ativo, '#1B6FD0', '#E9F4FD', '#155FB0')}
        disabled={inativasAbertas}
        onClick={() => mudar(() => setMarca(ativo ? '' : chave))}
        onTirar={() => tirar(chave)}
      />
    );
  };
  const mesesDasInativas = sel.length ? mesesMarcados : mesesPeriodo;

  const cartoes = inativasAbertas ? inativas.data : data;
  const qtdCartoes = cartoes?.empresas.length ?? 0;
  const totalCartoes = cartoes?.total ?? 0;
  let msgVazia = 'Nenhuma empresa neste recorte.';
  if (buscaAplicada) msgVazia = `Nenhuma empresa encontrada para “${buscaAplicada}”.`;
  else if (inativasAbertas) msgVazia = 'Nenhuma empresa inativa neste recorte.';

  let sufixoDoRodape = '';
  if (inativasAbertas) sufixoDoRodape = ' inativa(s)';
  else if (buscaAplicada) sufixoDoRodape = ' encontrada(s)';

  const temFiltroDaLista =
    sel.length > 0 || inativasAbertas || !!situacao || !!marca || !!etapa || excluir.length > 0;

  return (
    <div className={`fcmCart${escuro ? ' escuro' : ''}`}>
      <div className="brilhoSel" aria-hidden="true">
        <div className="brilhoFixo">
          <div className="bl blBase" style={{ opacity: gruposMarcados.length ? 0 : 1 }} />
          <div className="bl blNeutro" style={{ opacity: gruposMarcados.length ? 1 : 0 }} />
          <div className="bl blFech" style={{ opacity: acesa('fech') }} />
          <div className="bl blLib" style={{ opacity: acesa('lib') }} />
          <div className="bl blDiv" style={{ opacity: acesa('div') }} />
          <div className="bl blTrav" style={{ opacity: acesa('trav') }} />
          <Circuito />
        </div>
      </div>
      <div
        style={{
          maxWidth: 1360,
          margin: '0 auto',
          padding: '28px 32px 48px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
        }}
      >
        <header
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              aria-label="Voltar"
              onClick={() => navigate('/modulesHome/accountingConciliation')}
              style={{
                width: 36,
                height: 36,
                borderRadius: 999,
                border: '1px solid #E4E4E7',
                background: '#FFFFFF',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Voltar cor="#0F172A" />
            </button>
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '.1em',
                  textTransform: 'uppercase',
                  color: '#64748B',
                }}
              >
                Contábil
              </div>
              <h1
                style={{
                  margin: '2px 0 0',
                  fontSize: 26,
                  fontWeight: 700,
                  letterSpacing: '-.01em',
                }}
              >
                Fechamento Contábil Mensal
              </h1>
            </div>
            {(isFetching || cal.isFetching || inativas.isFetching) && (
              <Spinner size="sm" color="#1B6FD0" />
            )}
          </div>
          <button
            type="button"
            onClick={() => setEscuro(!escuro)}
            aria-pressed={escuro}
            style={{
              height: 36,
              padding: '0 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              borderRadius: 999,
              border: '1px solid rgba(255, 255, 255, .8)',
              background: 'rgba(255, 255, 255, .55)',
              color: '#0F172A',
              fontSize: 12.5,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: 'inset 0 1px 0 #FFFFFF, 0 4px 12px rgba(21, 95, 176, .12)',
            }}
          >
            {escuro ? (
              <Sol tam={15} cor="#0F172A" traco={2} />
            ) : (
              <Lua tam={15} cor="#0F172A" traco={2} />
            )}
            {escuro ? 'Tema claro' : 'Tema escuro'}
          </button>
        </header>

        {(isError || cal.isError) && (
          <section
            aria-label="Erro"
            style={{
              background: '#FFFFFF',
              border: '1px solid #E4E4E7',
              borderRadius: 16,
              padding: 20,
            }}
          >
            <span style={{ color: '#B91C1C', fontWeight: 600 }}>
              Não foi possível carregar a carteira.{' '}
              {statusDoErro(error ?? cal.error) === 403
                ? 'A visão da carteira exige acesso a todos os grupos de empresas.'
                : 'Tente de novo; se continuar, avise o TI.'}
            </span>
          </section>
        )}

        <Calendario
          ano={ano}
          semAnoAnterior={ano <= anoMinimo}
          semAnoProximo={ultimoMesDe(ano + 1, hoje) < 0}
          onAno={mudarAno}
          meses={mesesCal}
          hoje={hoje}
          sel={sel}
          onSel={novo =>
            mudar(() => {
              setSel(novo);
              setSub('');
              setInatSel([]);
            })
          }
          inatSel={inatSel}
          onInat={competencia =>
            mudar(() =>
              setInatSel(
                inatSel.includes(competencia)
                  ? inatSel.filter(c => c !== competencia)
                  : [...inatSel, competencia].sort(),
              ),
            )
          }
          regimes={regimes}
          porRegime={cal.data?.regimes ?? {}}
          onRegimes={r => mudar(() => setRegimes(r))}
        />

        <Painel
          pareceres={pareceres}
          alvo={alvoDosPareceres}
          parecer={parecer}
          onParecer={setParecer}
          painel={data?.painel}
          etapa={etapa}
          onEtapa={e => mudar(() => setEtapa(e))}
          onAbrirEmpresa={id => abrir(id, filtro.fim)}
        />

        <section
          aria-label="Carteira"
          style={{
            background: '#FFFFFF',
            border: '1px solid #E4E4E7',
            borderRadius: 16,
            padding: '18px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          {/* filtros fixos à direita; o título se ajusta no espaço que sobra */}
          <div className="cartTopo">
            <div className="cartTitulo">
              <span style={{ width: 10, height: 10, borderRadius: 3, background: corTitulo }} />
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{titulo}</h2>
              <span style={{ fontSize: 12.5, color: '#52525B' }}>{resumo}</span>
              {temFiltroDaLista && (
                <button
                  type="button"
                  onClick={() =>
                    mudar(() => {
                      setSel([]);
                      setInatSel([]);
                      setSituacao('');
                      setSub('');
                      setMarca('');
                      setEtapa('');
                      setExcluir([]);
                    })
                  }
                  style={{
                    border: 0,
                    background: 'transparent',
                    padding: 0,
                    color: '#1B6FD0',
                    fontSize: 12,
                    fontWeight: 600,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  limpar seleção
                </button>
              )}
              <button
                type="button"
                onClick={exportar}
                disabled={exportando || inativasAbertas || !data?.total}
                title={
                  inativasAbertas
                    ? 'Feche as inativas para exportar a relação da carteira'
                    : 'Baixa em Excel uma linha por empresa e competência, com os filtros desta tela'
                }
                style={{
                  marginLeft: 'auto',
                  border: '1.5px solid #E4E4E7',
                  background: '#FFFFFF',
                  borderRadius: 8,
                  padding: '4px 12px',
                  color: '#1B6FD0',
                  fontSize: 12,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  cursor: exportando ? 'wait' : 'pointer',
                  opacity: exportando || inativasAbertas || !data?.total ? 0.55 : 1,
                }}
              >
                {exportando ? 'Gerando…' : 'Exportar relação'}
              </button>
            </div>
            <div className="cartFiltros">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                  }}
                >
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label htmlFor="busca-empresa" style={ROTULO}>
                    Empresa
                  </label>
                  <div
                    role="group"
                    aria-label="Buscar por"
                    style={{
                      display: 'flex',
                      gap: 2,
                      padding: 2,
                      background: '#F1F5F9',
                      borderRadius: 6,
                    }}
                  >
                    {TIPOS_BUSCA.map(([k, rot]) => {
                      const ativo = tipoBusca === k;
                      return (
                        <button
                          type="button"
                          key={k}
                          onClick={() => mudar(() => setTipoBusca(k))}
                          aria-pressed={ativo}
                          style={{
                            padding: '2px 7px',
                            border: 0,
                            borderRadius: 4,
                            fontSize: 9.5,
                            fontWeight: 700,
                            letterSpacing: '.04em',
                            cursor: 'pointer',
                            background: ativo ? '#FFFFFF' : 'transparent',
                            color: ativo ? '#0F172A' : '#64748B',
                            boxShadow: ativo ? '0 1px 2px rgba(15, 23, 42, .12)' : 'none',
                          }}
                        >
                          {rot}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <input
                  id="busca-empresa"
                  value={busca}
                  onChange={ev => setBusca(ev.target.value)}
                  placeholder={TIPOS_BUSCA.find(t => t[0] === tipoBusca)?.[2]}
                  style={{
                    width: 280,
                    maxWidth: '100%',
                    height: 36,
                    boxSizing: 'border-box',
                    border: '1px solid #E4E4E7',
                    borderRadius: 8,
                    padding: '0 10px',
                    font: 'inherit',
                    fontSize: 12.5,
                    background: '#FFFFFF',
                  }}
                />
              </div>
              <PeriodoDaCarteira
                periodo={periodo}
                texto={textoPeriodo}
                anos={anos}
                hoje={hoje}
                onAplicar={p =>
                  mudar(() => {
                    setPeriodo(p);
                    setSel([]);
                    setInatSel([]);
                    setSub('');
                    const anoAlvo = Math.max(...p.anos);
                    if (!p.anos.includes(ano) || p.anos.length === 1) setAno(anoAlvo);
                  })
                }
                onAnterior={() => mover(-1)}
                onProximo={() => mover(1)}
                semAnterior={!destino(-1)}
                semProximo={!destino(1)}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="ordem-lista" style={ROTULO}>
                  Ordenar por
                </label>
                <select
                  id="ordem-lista"
                  value={ordem}
                  onChange={ev => mudar(() => setOrdem(ev.target.value as 'receita' | 'criticos'))}
                  style={{
                    height: 36,
                    border: '1px solid #E4E4E7',
                    borderRadius: 8,
                    font: 'inherit',
                    fontSize: 12.5,
                    padding: '0 8px',
                    background: '#FFFFFF',
                  }}
                >
                  <option value="receita">Por empresa · receita</option>
                  <option value="criticos">Por empresa · pontos críticos</option>
                  <option value="grupo" disabled>
                    Por grupo econômico (em breve)
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            {chipsSituacao.map(f =>
              f.exclusao ? (
                <ChipComX
                  key={f.chave}
                  rotulo={`${f.rotulo}: ${numero(f.qtd)}`}
                  tirado={excluir.includes(f.exclusao)}
                  ativo={f.ativo}
                  estilo={f.estilo}
                  disabled={inativasAbertas}
                  onClick={f.pick}
                  onTirar={() => tirar(f.exclusao)}
                />
              ) : (
                <button
                  type="button"
                  key={f.chave}
                  className="chip"
                  aria-pressed={f.ativo}
                  disabled={inativasAbertas}
                  onClick={f.pick}
                  style={f.estilo}
                >
                  {f.rotulo}: {numero(f.qtd)}
                  {f.x}
                </button>
              ),
            )}
            {marcaChip(
              'sistema_proprio',
              'Sistema próprio',
              contagens?.tirados?.sistema_proprio ?? contagens?.sistema_proprio ?? 0,
            )}
            {marcaChip(
              'txt_pendente',
              'TXT pendente',
              contagens?.tirados?.txt_pendente ?? contagens?.txt_pendente ?? 0,
            )}
            <ChipComX
              rotulo={`${
                inativasAbertas
                  ? `Inativas · ${faixaDeMeses(inatSel.map(c => Number(c.slice(5, 7)) - 1))}`
                  : 'Inativas'
              }: ${numero(inativasAbertas ? (inativas.data?.total ?? 0) : (contagens?.inativas ?? 0))}`}
              tirado={excluir.includes('inativas')}
              ativo={inativasAbertas}
              estilo={cpChip(inativasAbertas, '#475569', '#EEF2F6', '#334155')}
              disabled={false}
              title="Inativas no cadastro ou fora do período em que são clientes do escritório (cliente desde / até). O x tira dos cards as que têm revisão em outros meses do período."
              onClick={() => mudar(() => setInatSel(inativasAbertas ? [] : mesesDasInativas))}
              onTirar={() => tirar('inativas')}
            />
          </div>

          {grupoDoDetalhe && !inativasAbertas && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 8,
                marginTop: -4,
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '.05em',
                  textTransform: 'uppercase',
                  color: '#64748B',
                }}
              >
                Detalhe
              </span>
              {[
                {
                  chave: '' as SubSituacao | '',
                  rotulo: 'Todas',
                  qtd: contagens?.situacoes[grupoDoDetalhe.chave]?.qtd ?? 0,
                  dica: undefined as string | undefined,
                  semDado: false,
                },
                ...grupoDoDetalhe.subs.map(s => ({
                  chave: s.chave as SubSituacao | '',
                  rotulo: s.rotulo,
                  qtd: contagens?.situacoes[grupoDoDetalhe.chave]?.subs[s.chave] ?? 0,
                  dica: s.dica,
                  semDado: !!s.semDado,
                })),
              ].map(f => {
                const ativo = sub === f.chave;
                return (
                  <button
                    type="button"
                    key={f.chave || 'todas'}
                    className="chip"
                    aria-pressed={ativo}
                    disabled={f.semDado}
                    title={f.dica}
                    onClick={() => mudar(() => setSub(f.chave))}
                    style={{
                      ...cpChip(
                        ativo,
                        grupoDoDetalhe.cor,
                        grupoDoDetalhe.suave,
                        grupoDoDetalhe.texto,
                      ),
                      padding: '4px 11px',
                      fontSize: 11.5,
                    }}
                  >
                    {f.rotulo}: {numero(f.qtd)}
                  </button>
                );
              })}
            </div>
          )}

          {cartoes && qtdCartoes === 0 && (
            <p
              style={{
                margin: 0,
                padding: 24,
                textAlign: 'center',
                fontSize: 13,
                color: '#52525B',
                background: '#F8FAFC',
                borderRadius: 12,
              }}
            >
              {msgVazia}
            </p>
          )}
          {!cartoes && (
            <p
              style={{
                margin: 0,
                padding: 24,
                textAlign: 'center',
                fontSize: 13,
                color: '#52525B',
              }}
            >
              Carregando a carteira…
            </p>
          )}
          <div className="empGrid">
            {inativasAbertas
              ? inativas.data?.empresas.map(e => (
                  <CartaoInativa key={e.external_id} e={e} variosAnos={variosAnos} />
                ))
              : data?.empresas.map(linha => (
                  <CartaoDaEmpresa
                    key={linha.id}
                    linha={linha}
                    grupoFixo={grupoFixo}
                    variosAnos={variosAnos}
                    marcadas={linha.competencias
                      .map(m => m.competencia)
                      .filter(c =>
                        chavesMarcadas.has(chaveDaMarcada({ id: linha.id, competencia: c })),
                      )}
                    selecionado={linha.competencias.some(m =>
                      chavesMarcadas.has(
                        chaveDaMarcada({ id: linha.id, competencia: m.competencia }),
                      ),
                    )}
                    onSelecionar={competencia =>
                      marcar({ id: linha.id, razao: linha.razao, competencia })
                    }
                    onAbrir={competencia => abrir(linha.id, competencia)}
                  />
                ))}
          </div>
          <Paginacao
            rodape={`Mostrando ${qtdCartoes} de ${numero(totalCartoes)}${sufixoDoRodape}`}
            pagina={cartoes?.pagina ?? 1}
            paginas={cartoes?.paginas ?? 1}
            onPagina={setPagina}
          />
        </section>

        {marcadas.length > 0 && (
          <DetalheDaSelecao
            marcadas={marcadas}
            atual={noCarrossel}
            onIr={setNoCarrossel}
            onLimpar={() => {
              setMarcadas([]);
              setNoCarrossel(0);
            }}
            onAbrirTela={(e: IEmpresaMarcada, aba: AbaDoDetalhe) => abrir(e.id, e.competencia, aba)}
          />
        )}
        {data && marcadas.length === 0 && (
          <OQueFalta
            titulo={titulo}
            porEmpresa={data.o_que_falta}
            porProblema={data.por_problema}
            onAbrir={i => abrir(i.id, i.competencia, TELA_DO_MODULO[i.modulo] ?? 'resumo')}
          />
        )}
      </div>
    </div>
  );
}

/** Status HTTP da falha, quando houve resposta do servidor. */
function statusDoErro(erro: unknown): number | undefined {
  return (erro as { response?: { status?: number } } | null)?.response?.status;
}
